import { FC, ReactElement, useMemo } from 'react';

import { RadioList } from '../../../../../compositions';
import trans from '../../../../../helpers/trans';
import {
    EducationLevel,
    EducationSubLevel,
    LearningPath,
    transformEducationLabelToFormOption,
} from '../../../../../models/EducationLevels';
import { FormOption } from '../../../../../types';
import { SchoolExperienceFormErrors } from '../../validations';

import './EducationLevelSelector.scss';

interface EducationLevelSelectorProps {
    educationLevel?: string;
    educationSubLevel?: string;
    learningPath?: string;
    educationLevelOptions: FormOption[];
    educationSubLevelOptions: Record<EducationLevel, EducationSubLevel[]>;
    learningPathOptions: Record<EducationLevel, LearningPath[]>;
    formErrors: SchoolExperienceFormErrors;
    onEducationLevelChange: (educationLevel: EducationLevel) => void;
    onEducationSubLevelChange: (educationSubLevel?: EducationSubLevel) => void;
    onLearningPathChange: (learningPath?: LearningPath) => void;
    className?: string;
}

const EducationLevelSelector: FC<EducationLevelSelectorProps> = ({
    educationLevel = '',
    educationSubLevel,
    learningPath,
    educationLevelOptions,
    educationSubLevelOptions,
    learningPathOptions,
    formErrors,
    onEducationLevelChange,
    onEducationSubLevelChange,
    onLearningPathChange,
    className = '',
}): ReactElement => {
    const relatedEducationSubLevelOptions = useMemo<FormOption[]>(() => {
        const relatedEducationSubLevels = educationSubLevelOptions[educationLevel as EducationLevel] || [];

        return relatedEducationSubLevels.map(transformEducationLabelToFormOption);
    }, [educationLevel, educationSubLevelOptions]);

    const relatedEducationLearningPathOptions = useMemo<FormOption[]>(() => {
        const relatedLearningPaths = learningPathOptions[educationLevel as EducationLevel] || [];

        return relatedLearningPaths.map(transformEducationLabelToFormOption);
    }, [educationLevel, educationSubLevelOptions]);

    const handleEducationLevelChange = (value: string): void => {
        onEducationLevelChange(value as EducationLevel);
    };

    const handleEducationSubLevelChange = (value: string): void => {
        onEducationSubLevelChange(value as EducationSubLevel);
    };

    const handleLearningPathChange = (value: string): void => {
        onLearningPathChange(value as LearningPath);
    };

    return (
        <div className={`education-level-selector ${className}`}>
            <RadioList
                label={trans('forms.schoolExperience.input.educationLevel')}
                name="education-level"
                required
                options={educationLevelOptions}
                value={educationLevel}
                error={formErrors.educationLevel}
                onChange={handleEducationLevelChange}
                className="education-level-selector__input"
            />

            {relatedEducationSubLevelOptions.length > 0 && (
                <RadioList
                    label={trans('forms.schoolExperience.input.educationSubLevel')}
                    name="education-sub-level"
                    required
                    options={relatedEducationSubLevelOptions}
                    value={educationSubLevel || ''}
                    error={formErrors.educationSubLevel}
                    onChange={handleEducationSubLevelChange}
                    className="education-level-selector__input"
                />
            )}

            {relatedEducationLearningPathOptions.length > 0 && (
                <RadioList
                    label={trans('forms.schoolExperience.input.learningPath')}
                    name="learning-path"
                    required
                    options={relatedEducationLearningPathOptions}
                    value={learningPath || ''}
                    error={formErrors.learningPath}
                    onChange={handleLearningPathChange}
                    className="education-level-selector__input"
                />
            )}
        </div>
    );
};

export default EducationLevelSelector;
